/**
 * Names for principals in one workspace (users, service accounts, groups), so screens can
 * show "Anna (user)" instead of "user:12". System-admin reads; refetched after any change.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Container, Group, RoleAssignment, ServiceAccount, User, WorkflowDefinition, type Principal } from "@workcore/schemas";
import { get } from "./api";

export const wsKey = (slug: string, what: string) => ["ws", slug, what];

export function useUsers(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "users"), queryFn: async () => z.array(User).parse(await get(`/v1/ws/${slug}/users`)) });
}
export function useGroups(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "groups"), queryFn: async () => z.array(Group).parse(await get(`/v1/ws/${slug}/groups`)) });
}
export function useServiceAccounts(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "service-accounts"), queryFn: async () => z.array(ServiceAccount).parse(await get(`/v1/ws/${slug}/service-accounts`)) });
}
export function useRoles(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "roles"), queryFn: async () => z.array(RoleAssignment).parse(await get(`/v1/ws/${slug}/roles`)) });
}
export function useDefinitions(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "definitions"), queryFn: async () => z.array(WorkflowDefinition).parse(await get(`/v1/ws/${slug}/definitions`)) });
}
export function useContainers(slug: string) {
  return useQuery({ queryKey: wsKey(slug, "containers"), queryFn: async () => z.array(Container).parse(await get(`/v1/ws/${slug}/containers`)) });
}

/** Invalidate everything for a workspace after a write; the lists are small. */
export function useRefresh(slug: string): () => void {
  const q = useQueryClient();
  return () => void q.invalidateQueries({ queryKey: ["ws", slug] });
}

export interface Directory {
  label(p: Principal): string;
  options: { value: string; label: string; kind: Principal["kind"] }[];
}

export function useDirectory(slug: string): Directory {
  const users = useUsers(slug).data ?? [];
  const groups = useGroups(slug).data ?? [];
  const sas = useServiceAccounts(slug).data ?? [];
  const names = new Map<string, string>();
  for (const u of users) names.set(`user:${u.id}`, `${u.displayName}${u.active ? "" : " (inactive)"}`);
  for (const g of groups) names.set(`group:${g.id}`, g.name);
  for (const s of sas) names.set(`service_account:${s.id}`, `${s.name}${s.active ? "" : " (inactive)"}`);
  return {
    label: (p) => names.get(`${p.kind}:${p.id}`) ?? `${p.kind}:${p.id}`,
    options: [
      ...users.filter((u) => u.active).map((u) => ({ value: `user:${u.id}`, label: `${u.displayName} <${u.email}>`, kind: "user" as const })),
      ...groups.map((g) => ({ value: `group:${g.id}`, label: `${g.name} (group)`, kind: "group" as const })),
      ...sas.filter((s) => s.active).map((s) => ({ value: `service_account:${s.id}`, label: `${s.name} (service account)`, kind: "service_account" as const })),
    ],
  };
}

export function parsePrincipal(v: string): Principal {
  const [kind, id] = v.split(":");
  return { kind: kind as Principal["kind"], id: Number(id) };
}

export const KIND_LABEL: Record<Principal["kind"], string> = { user: "user", group: "group", service_account: "service account" };
